import { useState } from 'react';
import StyledSnackbar from 'src/components/StyledComponents/StyledSnackBar';
import {
  RawMaterialCategory,
  BasicRawMaterialCategory,
} from 'src/models/Management.interface';
import { apiUrls, errorMessages } from 'src/utils/properties.utils';
import { axiosTokenInstance } from 'src/utils/axiosInstance.utils';
import { getApiErrorMessage } from 'src/utils/errorHandler';

interface PaginatedCategoryResponse {
  data: RawMaterialCategory[];
  totalItems: number;
}

export const useGetRawMaterialCategory = () => {
  const [categories, setCategories] = useState<RawMaterialCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalItems, setTotalItems] = useState(0);
  const { showSnackbar } = StyledSnackbar();

  const fetchCategories = async (
    page = 1,
    limit = 15,
    search = '',
    sortBy = 'createdAt',
    order = 'DESC'
  ) => {
    setLoading(true);
    try {
      const response = await axiosTokenInstance.get<PaginatedCategoryResponse>(
        apiUrls.RAW_MATERIAL_CATEGORY_LIST,
        {
          params: {
            offset: page,
            limit: limit,
            search: search,
            sortBy: sortBy,
            order: order,
          },
          withCredentials: true,
        }
      );

      setCategories(response.data.data || []);
      setTotalItems(response.data.totalItems || 0);
    } catch (error: any) {
      if (error.response?.status === 404) {
        console.error(error.response.data.message);
        setCategories([]);
      } else {
        showSnackbar(errorMessages.SOMETHING_WENT_WRONG, 'error');
      }
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 800);
    }
  };

  const deleteCategory = async (id: string) => {
    try {
      const response = await axiosTokenInstance.delete(
        apiUrls.DELETE_RAW_MATERIAL_CATEGORY,
        {
          params: { id },
        }
      );
      showSnackbar(response.data.message, 'success');
      return true;
    } catch (error) {
      const errorMessage = await getApiErrorMessage(error);
      showSnackbar(errorMessage, 'error');
      return false;
    }
  };

  return {
    loading,
    categories,
    totalItems,
    fetchCategories,
    deleteCategory,
  };
};

export const getBasicRawMaterialCategories = async () => {
  const response = await axiosTokenInstance.get(
    apiUrls.RAW_MATERIAL_CATEGORY_LIST_BASIC
  );
  return response.data.data;
};

export const useGetBasicRawMaterialCategories = () => {
  const [basicCategories, setBasicCategories] = useState<
    BasicRawMaterialCategory[]
  >([]);
  const { showSnackbar } = StyledSnackbar();

  const fetchBasicCategories = async () => {
    try {
      const categoryData = await getBasicRawMaterialCategories();
      setBasicCategories(categoryData || []);
    } catch (error) {
      showSnackbar(errorMessages.SOMETHING_WENT_WRONG, 'error');
      setBasicCategories([]);
    }
  };

  return {
    basicCategories,
    fetchBasicCategories,
  };
};
